import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  usePackageList,
  useFunctions,
  useCreatePackage,
  useUpdatePackage,
  useTogglePackageStatus,
  useDeletePackage,
  usePackageDetail,
} from '../hooks';
import type { PackageFilters, PackageFormValues, PackageRecord } from '../types';
import { PackageTable } from '../components/PackageTable';
import { PackageFormModal } from '../components/PackageFormModal';

const defaultFilters: PackageFilters = {
  status: 'all',
  search: '',
};

export function PackageListPage() {
  const [filters, setFilters] = useState<PackageFilters>(defaultFilters);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data: packages = [], isLoading } = usePackageList(filters);
  const { data: functions = [] } = useFunctions();

  const {
    data: editingPackage,
    isFetching: isFetchingPackage,
  } = usePackageDetail(editingId ?? undefined, modalMode === 'edit' && isModalOpen);

  const createMutation = useCreatePackage();
  const updateMutation = useUpdatePackage();
  const toggleMutation = useTogglePackageStatus();
  const deleteMutation = useDeletePackage();

  const openCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditModal = (record: PackageRecord) => {
    setModalMode('edit');
    setEditingId(record.id);
    setModalOpen(true);
  };

  const handleSubmit = (values: PackageFormValues) => {
    if (values.function_ids.length === 0) {
      toast.error('Vui lòng chọn tối thiểu 1 chức năng cho gói');
      return;
    }

    if (modalMode === 'create') {
      createMutation.mutate(values, {
        onSuccess: () => {
          setModalOpen(false);
        },
      });
    } else if (editingId) {
      updateMutation.mutate(
        {
          id: editingId,
          payload: values,
        },
        {
          onSuccess: () => setModalOpen(false),
        }
      );
    }
  };

  const handleToggleStatus = (record: PackageRecord) => {
    toggleMutation.mutate({
      id: record.id,
      status: record.status === 'active' ? 'inactive' : 'active',
    });
  };

  const handleDelete = (record: PackageRecord) => {
    const confirmed = window.confirm('Bạn có chắc muốn xóa (soft delete) gói này?');
    if (!confirmed) return;
    deleteMutation.mutate(record.id);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const confirmed = window.confirm(`Xóa (soft delete) ${selectedIds.length} gói đã chọn?`);
    if (!confirmed) return;
    try {
      await Promise.all(selectedIds.map((id) => deleteMutation.mutateAsync(id)));
      setSelectedIds([]);
    } catch (error) {
      console.error(error);
    }
  };

  const modalInitialValues = useMemo(() => {
    if (modalMode === 'edit' && editingPackage) {
      return {
        name: editingPackage.name,
        code: editingPackage.code,
        origin_price: editingPackage.origin_price,
        sale_price: editingPackage.sale_price,
        position: editingPackage.position,
        description: editingPackage.description,
        status: editingPackage.status,
        function_ids: editingPackage.functions?.map((fn) => fn.id) ?? [],
      };
    }
    return undefined;
  }, [editingPackage, modalMode]);

  const submitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="card">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
        <div>
          <h3 className="card-title mb-1">Quản lý gói dịch vụ</h3>
          <p className="card-subtitle mb-0">
            Quản trị danh sách gói SaaS, cấu hình giá và chức năng đi kèm để phục vụ đăng ký của khách hàng.
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-primary btn-sm" onClick={openCreateModal} style={{ padding: '6px 12px', borderRadius: '10px' }}>
            <span className="ti ti-plus" style={{ fontSize: '16px', marginRight: '6px' }} />
            Thêm gói mới
          </button>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            disabled={selectedIds.length === 0 || deleteMutation.isLoading}
            onClick={handleBulkDelete}
            style={{ padding: '6px 12px', borderRadius: '10px' }}
          >
            <span className="ti ti-trash" style={{ fontSize: '16px', marginRight: '6px' }} />
            Xóa đã chọn
          </button>
        </div>
      </div>

      <PackageTable
        data={packages}
        isLoading={isLoading}
        filters={filters}
        onFiltersChange={setFilters}
        onEdit={openEditModal}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDelete}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      <PackageFormModal
        open={isModalOpen}
        mode={modalMode}
        initialValues={modalInitialValues}
        functions={functions}
        submitting={submitting || isFetchingPackage}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}


